import { apiClient } from '@/api/api-client';
import { AUTH_STORAGE_KEYS, getApiUrl } from '@/lib/auth-utils';
import { ApiResponse } from '@/types/api-response';
import {
    ChannelPerformanceResponse,
    CustomerSalesResponse,
    PaymentAnalysisResponse,
    ProductPerformanceResponse,
    SalesSummaryResponse,
    TimeSeriesGrouping,
    TimeSeriesSalesResponse,
} from '@/types/report-type';

export async function getSalesSummary(
    fromDate: string,
    toDate: string
): Promise<ApiResponse<SalesSummaryResponse>> {
    const response = await apiClient.get<SalesSummaryResponse, { fromDate: string; toDate: string }>(
        `/reports/sales/summary`,
        { fromDate, toDate }
    );
    return response;
}

export async function getProductPerformanceReport(
    fromDate: string,
    toDate: string,
    limit?: number
): Promise<ApiResponse<ProductPerformanceResponse>> {
    const params: { fromDate: string; toDate: string; limit?: number } = { fromDate, toDate };
    if (limit !== undefined) {
        params.limit = limit;
    }
    const response = await apiClient.get<ProductPerformanceResponse, typeof params>(
        `/reports/sales/products`,
        params
    );
    return response;
}

export async function getCustomerSalesReport(
    fromDate: string,
    toDate: string,
    limit?: number
): Promise<ApiResponse<CustomerSalesResponse>> {
    const params: { fromDate: string; toDate: string; limit?: number } = { fromDate, toDate };
    if (limit !== undefined) {
        params.limit = limit;
    }
    const response = await apiClient.get<CustomerSalesResponse, typeof params>(
        `/reports/sales/customers`,
        params
    );
    return response;
}

export async function getChannelPerformanceReport(
    fromDate: string,
    toDate: string
): Promise<ApiResponse<ChannelPerformanceResponse>> {
    const response = await apiClient.get<ChannelPerformanceResponse, { fromDate: string; toDate: string }>(
        `/reports/sales/channels`,
        { fromDate, toDate }
    );
    return response;
}

export async function getTimeSeriesSalesReport(
    fromDate: string,
    toDate: string,
    grouping: TimeSeriesGrouping
): Promise<ApiResponse<TimeSeriesSalesResponse>> {
    const response = await apiClient.get<
        TimeSeriesSalesResponse,
        { fromDate: string; toDate: string; grouping: TimeSeriesGrouping }
    >(`/reports/sales/time-series`, { fromDate, toDate, grouping });
    return response;
}

export async function getPaymentAnalysisReport(
    fromDate: string,
    toDate: string
): Promise<ApiResponse<PaymentAnalysisResponse>> {
    const response = await apiClient.get<PaymentAnalysisResponse, { fromDate: string; toDate: string }>(
        `/reports/sales/payments`,
        { fromDate, toDate }
    );
    return response;
}

export async function downloadSalesReport(params: {
    fromDate: string;
    toDate: string;
    format: 'excel' | 'pdf';
    orderStatus?: string;
    paymentStatus?: string;
    source?: string;
}): Promise<void> {
    const { fromDate, toDate, format, orderStatus, paymentStatus, source } = params;

    const queryParams = new URLSearchParams({
        fromDate,
        toDate,
        format,
    });

    if (orderStatus) queryParams.append('orderStatus', orderStatus);
    if (paymentStatus) queryParams.append('paymentStatus', paymentStatus);
    if (source) queryParams.append('source', source);

    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const response = await fetch(`${getApiUrl('/reports/sales/download')}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': format === 'excel'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream'
                : 'application/pdf,application/octet-stream',
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to download report: ${response.statusText}`);
    }

    // Verify content type
    const contentType = response.headers.get('Content-Type');
    console.log('Response Content-Type:', contentType);
    console.log('Response headers:', Array.from(response.headers.entries()));

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `sales-report-${fromDate}-to-${toDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`;

    if (contentDisposition) {
        // Try to match filename with quotes: filename="example.xlsx"
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (!filenameMatch) {
            // Try to match filename without quotes: filename=example.xlsx
            filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/);
        }
        if (!filenameMatch) {
            // Try to match filename* (RFC 5987): filename*=UTF-8''example.xlsx
            filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = decodeURIComponent(filenameMatch[1]);
            }
        } else if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
        }
    }

    // Create blob and download with proper MIME type
    const blob = await response.blob();

    // Ensure blob has correct MIME type
    const mimeType = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
    const typedBlob = new Blob([blob], { type: mimeType });

    const url = window.URL.createObjectURL(typedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Clean up after a short delay to ensure download starts
    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 100);
}
